import { BUBBLE_R, CUSHION, OMNOM_R, STAR_R } from '../engine/constants';
import { ASPECT, WORLD_H, type Pt } from '../engine/level';
import type { GameWorld, Snapshot } from '../engine/world';

export class Renderer {
  readonly ctx: CanvasRenderingContext2D;
  dpr = 1; w = 0; h = 0; ox = 0; oy = 0; scale = 1;
  constructor(readonly canvas: HTMLCanvasElement) { this.ctx = canvas.getContext('2d')!; }
  resize(w: number, h: number, dpr: number) {
    this.dpr = Math.min(dpr, 2); this.w = w; this.h = h;
    this.canvas.width = Math.round(w * this.dpr); this.canvas.height = Math.round(h * this.dpr);
    this.canvas.style.width = `${w}px`; this.canvas.style.height = `${h}px`;
    this.scale = Math.min(h / WORLD_H, w / (ASPECT * WORLD_H));
    this.ox = (w - ASPECT * WORLD_H * this.scale) / 2; this.oy = (h - WORLD_H * this.scale) / 2;
  }
  toScreen(p: Pt): Pt { return [this.ox + p[0] * this.scale, this.oy + (WORLD_H - p[1]) * this.scale]; }
  toWorld(x: number, y: number): Pt { return [(x - this.ox) / this.scale, WORLD_H - (y - this.oy) / this.scale]; }

  draw(w: GameWorld, snap: Snapshot, trail: Pt[]) {
    const { ctx, scale } = this;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = '#d8ccb6'; ctx.fillRect(0, 0, this.w, this.h);
    ctx.fillStyle = '#efe6d5'; ctx.fillRect(this.ox, this.oy, ASPECT * WORLD_H * scale, WORLD_H * scale);
    // 가시
    for (const s of snap.spikes) {
      const a = this.toScreen(s.a), b = this.toScreen(s.b);
      ctx.strokeStyle = '#6d6d6d'; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke();
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]), n = Math.max(2, Math.round(len / 10)), nx = -(b[1] - a[1]) / len, ny = (b[0] - a[0]) / len;
      ctx.fillStyle = '#8a8a8a'; ctx.beginPath();
      for (let i = 0; i < n; i++) { const t0 = i / n, t1 = (i + 0.5) / n, t2 = (i + 1) / n; ctx.moveTo(a[0] + (b[0] - a[0]) * t0, a[1] + (b[1] - a[1]) * t0); ctx.lineTo(a[0] + (b[0] - a[0]) * t1 + nx * 9, a[1] + (b[1] - a[1]) * t1 + ny * 9); ctx.lineTo(a[0] + (b[0] - a[0]) * t2, a[1] + (b[1] - a[1]) * t2); }
      ctx.fill();
    }
    // 쿠션
    for (const c of snap.cushions) {
      const p = this.toScreen(c.p); const cw = CUSHION.w * scale, ch = CUSHION.h * scale;
      ctx.fillStyle = '#4aa3df'; ctx.beginPath(); ctx.roundRect(p[0] - cw / 2, p[1] - ch / 2, cw, ch, 4); ctx.fill();
      const d = { n: [0, -1], s: [0, 1], e: [1, 0], w: [-1, 0] }[c.dir];
      ctx.strokeStyle = c.puff > 0 ? '#1f6fb0' : '#cfe8f7'; ctx.lineWidth = 3; ctx.beginPath();
      ctx.moveTo(p[0], p[1]); ctx.lineTo(p[0] + d[0]! * cw * 0.9, p[1] + d[1]! * cw * 0.9); ctx.stroke();
    }
    // 밧줄
    for (const r of snap.ropes) {
      if (r.auto !== undefined && !r.attached) {
        // 자동 고리: 점선 원
        const p = this.toScreen(r.hook); ctx.setLineDash([4, 4]); ctx.strokeStyle = 'rgba(59,111,182,.6)'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(p[0], p[1], r.auto * scale, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
        continue;
      }
      if (r.cut) continue;
      const pts = w.ropePolyline(r, snap.candy).map((p) => this.toScreen(p));
      ctx.strokeStyle = '#7a4b22'; ctx.lineWidth = Math.max(3, 0.012 * WORLD_H * scale); ctx.lineCap = 'round'; ctx.beginPath();
      pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.stroke();
    }
    // 고리
    for (const r of snap.ropes) {
      const p = this.toScreen(r.hook);
      ctx.fillStyle = '#3b6fb6'; ctx.beginPath(); ctx.arc(p[0], p[1], 7, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#efe6d5'; ctx.beginPath(); ctx.arc(p[0], p[1], 3, 0, Math.PI * 2); ctx.fill();
    }
    // 별
    for (const s of snap.starPos) {
      if (!s) continue;
      const p = this.toScreen(s); this.star(p[0], p[1], STAR_R * scale);
    }
    // 거품(안 터진 것)
    for (const b of snap.bubbles) {
      if (b.popped) continue;
      const p = this.toScreen(b.p);
      ctx.strokeStyle = 'rgba(80,140,220,.8)'; ctx.fillStyle = 'rgba(120,180,240,.18)'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(p[0], p[1], BUBBLE_R * scale, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    }
    // 개구리
    { const p = this.toScreen(snap.omnom); const r = OMNOM_R * scale;
      ctx.fillStyle = '#5cb85c'; ctx.beginPath(); ctx.arc(p[0], p[1], r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(p[0] - r * 0.35, p[1] - r * 0.3, r * 0.22, 0, Math.PI * 2); ctx.arc(p[0] + r * 0.35, p[1] - r * 0.3, r * 0.22, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#222'; ctx.beginPath(); ctx.arc(p[0] - r * 0.35, p[1] - r * 0.3, r * 0.1, 0, Math.PI * 2); ctx.arc(p[0] + r * 0.35, p[1] - r * 0.3, r * 0.1, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#2d6a2d'; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(p[0], p[1] + r * 0.15, r * 0.5, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke();
    }
    // 사탕(+거품)
    { const p = this.toScreen(snap.candy); const r = snap.candyR * scale;
      if (snap.inBubble) { ctx.strokeStyle = 'rgba(80,140,220,.8)'; ctx.fillStyle = 'rgba(120,180,240,.18)'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(p[0], p[1], BUBBLE_R * scale, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); }
      ctx.fillStyle = '#fff'; ctx.strokeStyle = '#c33'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(p[0], p[1], r, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.save(); ctx.beginPath(); ctx.arc(p[0], p[1], r, 0, Math.PI * 2); ctx.clip();
      ctx.fillStyle = '#e04848'; ctx.fillRect(p[0] - r * 0.35, p[1] - r, r * 0.25, r * 2); ctx.fillRect(p[0] + r * 0.1, p[1] - r, r * 0.25, r * 2);
      ctx.restore();
    }
    // 손가락 궤적
    if (trail.length > 1) {
      ctx.strokeStyle = 'rgba(255,255,255,.7)'; ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.beginPath();
      trail.forEach((p, i) => { const s = this.toScreen(p); i ? ctx.lineTo(s[0], s[1]) : ctx.moveTo(s[0], s[1]); }); ctx.stroke();
    }
  }
  private star(x: number, y: number, r: number) {
    const { ctx } = this; ctx.fillStyle = '#f2c62d'; ctx.beginPath();
    for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? r * 0.45 : r; ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr); }
    ctx.closePath(); ctx.fill();
  }
}
